/**
 * A message carrying `metadata.meeting` (TRUS-691) rendered as the meeting
 * card. The live meeting comes from GET /meetings/{id}; until it loads (or if
 * the viewer may not read it) the message's own title + time are shown.
 */

import React from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import type { MeetingMessageMeta } from '@/services/meetingsApi';
import { parseCallTime } from '@/services/callsApi';
import { MeetingCard } from './MeetingCard';
import { formatMeetingRange } from './meetingTime';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { ItemZoneNote } from '@/components/ui/ItemZoneNote';
import { useMeeting } from './useMeetings';
import { GuestLinkSlot } from './meetingSlots';

interface MeetingMessageCardProps {
  meta: MeetingMessageMeta;
  currentUserId?: number | null;
  currentMemberId?: number | null;
  tone?: 'default' | 'inverted';
}

export function MeetingMessageCard({ meta, currentUserId, currentMemberId, tone = 'default' }: MeetingMessageCardProps) {
  const { t, language } = useAppTranslation();
  const tz = useUserTimezone();
  const q = useMeeting(meta.meeting_id);
  if (q.data) {
    return (
      <MeetingCard
        meeting={q.data}
        currentUserId={currentUserId}
        currentMemberId={currentMemberId}
        tone={tone}
        footer={<GuestLinkSlot meeting={q.data} />}
      />
    );
  }
  const inverted = tone === 'inverted';
  return (
    <div className="flex items-center gap-3 py-1" data-testid={`meeting-card-${meta.meeting_id}`} data-phase="loading">
      <div className={cn('flex h-9 w-9 items-center justify-center rounded-full', inverted ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary')}>
        {q.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
      </div>
      <div className="min-w-0">
        <div className={cn('truncate text-sm font-semibold', inverted && 'text-white')}>
          {meta.title || t('messaging.meetings.untitled', 'Meeting')}
        </div>
        <div className={cn('text-xs', inverted ? 'text-white/80' : 'text-muted-foreground')}>
          {meta.starts_at
            ? formatMeetingRange(
                meta.starts_at,
                meta.duration_min ? new Date((parseCallTime(meta.starts_at) ?? 0) + meta.duration_min * 60_000).toISOString() : null,
                language,
                tz,
              )
            : q.isError
              ? t('messaging.meetings.unavailable', 'This meeting is not available to you.')
              : null}
          {meta.starts_at && <ItemZoneNote value={meta.starts_at} itemTimeZone={meta.timezone} kind="meeting" className="ml-1" />}
        </div>
      </div>
    </div>
  );
}

export default MeetingMessageCard;
